import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, XIcon } from 'lucide-react';
import { clusterById } from '../../../data/teaser';
import { bayesProbe, irreversible } from '../../../data/snak2';
import { ClusterImage } from '../../ClusterImage';
import { BayesianOverlay } from '../../BayesianOverlay';
const EASE = [0.23, 1, 0.32, 1] as const;
export function IrreversibilityScreen() {
  const [probeOpen, setProbeOpen] = useState(false);
  const early = clusterById(irreversible.compareEarly);
  const late = clusterById(irreversible.compareLate);
  return <div className="relative flex flex-1 flex-col" data-id="element-657" data-mp="1a2w6y503h75w">
      <div className="flex items-start justify-between gap-3" data-id="element-658" data-mp="1a3ntkqbpruk1">
        <motion.h2 initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.26,
        ease: EASE
      }} className="font-display text-[25px] font-semibold leading-[1.08] tracking-tight text-ozNavy" data-id="element-659" data-mp="15al4ovhvt9a6">
          {irreversible.headline}
        </motion.h2>

        <motion.button type="button" onClick={() => setProbeOpen(true)} animate={{
        opacity: [1, 0.5, 1]
      }} transition={{
        duration: 1.6,
        repeat: Infinity,
        ease: 'linear'
      }} className="mt-1 shrink-0 rounded-full bg-ozGreen px-3 py-1.5 text-[10.5px] font-semibold text-ozNavy transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ozBlue focus-visible:ring-offset-2" data-id="element-660" data-mp="3j7s4jdb5vrn6">
          {bayesProbe.trigger}
        </motion.button>
      </div>

      <p className="mt-3 text-[12px] leading-relaxed text-mut" data-id="element-661" data-mp="3a7wrf2y1pmki">{irreversible.lead}</p>

      <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-662" data-mp="3f6q5oxl5phlq">
        {[{
        cluster: early,
        label: irreversible.compareEarlyLabel,
        late: false
      }, {
        cluster: late,
        label: irreversible.compareLateLabel,
        late: true
      }].map((panel, i) => <motion.figure key={panel.cluster.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.26,
        delay: 0.08 + i * 0.06,
        ease: EASE
      }} className={`overflow-hidden rounded-xl border ${panel.late ? 'border-ozBlue' : 'border-line'}`} data-id="element-663" data-mp="uf69fg8e4ltp" data-mpi={i}>
            <ClusterImage cluster={panel.cluster} zoom className="aspect-[4/3]" data-id="element-664" data-mp="2vigtofdiaune" />
            <figcaption className="bg-mist px-2 py-1.5" data-id="element-665" data-mp="27sw59gyl356q">
              <span className="block text-[9px] uppercase tracking-micro text-mut" data-id="element-666" data-mp="27te0qbdf9fdl">
                {panel.cluster.name}
              </span>
              <span className="mt-0.5 block text-[10.5px] font-semibold leading-tight text-ozNavy" data-id="element-667" data-mp="2glzhxn1ene0b">
                {panel.label}
              </span>
            </figcaption>
          </motion.figure>)}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3" data-id="element-668" data-mp="1o43m70pex0h2">
        <div data-id="element-669" data-mp="1o2sjl47pdyrj">
          <p className="flex items-center gap-1.5 text-[9px] uppercase tracking-micro text-ozGreen" data-id="element-670" data-mp="1k3tftmxpk81o">
            <CheckIcon className="h-3 w-3 shrink-0" aria-hidden="true" data-id="element-671" data-mp="19zwkmi19jtfl" />
            {irreversible.reversibleLabel}
          </p>
          <ul className="mt-2 flex flex-col gap-1" data-id="element-672" data-mp="1erekf2rjvnyz">
            {irreversible.reversibleItems.map(item => <li key={item} className="text-[11px] leading-snug text-mut" data-id="element-673" data-mp="1030a66rt0x8e">
                {item}
              </li>)}
          </ul>
        </div>
        <div className="border-l border-line pl-3" data-id="element-674" data-mp="1spl0r50gh5aw">
          <p className="flex items-center gap-1.5 text-[9px] uppercase tracking-micro text-ozBlue" data-id="element-675" data-mp="1sqw2kzacc2zd">
            <XIcon className="h-3 w-3 shrink-0" aria-hidden="true" data-id="element-676" data-mp="1xofrct4nzy5k" />
            {irreversible.irreversibleLabel}
          </p>
          <ul className="mt-2 flex flex-col gap-1" data-id="element-677" data-mp="1nr1bovqkfdeb">
            {irreversible.irreversibleItems.map(item => <li key={item} className="text-[11px] font-medium leading-snug text-ozNavy" data-id="element-678" data-mp="7c9vrialvylw">
                {item}
              </li>)}
          </ul>
        </div>
      </div>

      <motion.p initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.26,
      delay: 0.36,
      ease: EASE
    }} className="mt-auto pt-5 font-display text-[21px] font-semibold leading-snug text-ozNavy" data-id="element-679" data-mp="sj0rkwx88v6k">
        {irreversible.takeaway}
      </motion.p>

      <AnimatePresence data-id="element-680" data-mp="dumqaqfgles4">
        {probeOpen && <BayesianOverlay probe={bayesProbe} onClose={() => setProbeOpen(false)} data-id="element-681" data-mp="dvo24x4au5aw" />}
      </AnimatePresence>
    </div>;
}